/**
 * Rig-avatar builder (E2.4.S1) — composes the resolve chain + loader + appearance
 * application into one avatar assembly: resolve the rig pack, instantiate the base
 * body at lod0, apply the character's body-type morph, and attach the resolved
 * wardrobe at the head/body attach. Returns the assembled root, or `null` for the
 * graceful capsule fallback (no rig pack, or the body failed to load) — the caller
 * (E2.4.S2) then keeps the existing capsule+nose placeholder.
 *
 * NO Game.ts wiring in this story — this is the composition unit S2 will drive on
 * the self + remote player paths (reusing the E2.3.S2 upgrade-in-place + dispose
 * pattern). Self/other-agnostic: appearance is a full-state field on every player
 * entity (AD-137), so one builder serves self and remotes.
 *
 * Consumes the shared `PackLoaderCache` (the E2.3.S2 generalisation of E2.2.S2's
 * pack-loader acquisition — the E2.4.S1 fold target the E2.2.S5 gate queued as
 * E22-F3) rather than duplicating loader wiring. Node-safe: the only `three`
 * dependency is the `Object3D` TYPE (erased at runtime); the loader is injected and
 * the avatar is manipulated through structural slices, so the whole build runs under
 * `node --test` with fakes (AD-120). Import boundary (AD-122): shared types via
 * `@great-cloud/shared/client`.
 */

import { PropertyBinding, type AnimationClip as RigAnimationClip, type Object3D } from 'three';

import type { AssetPackDef, CharacterAppearance } from '@great-cloud/shared/client';

import { PackLoaderCache, disposeObject3D, type GltfLoadFn } from '../assets/index.js';
import { COVERED_BY_SLOT } from '../equipment/renderer.js';
import { applyBodyMorph } from './morphs.js';
import {
  RIG_BODY_ACTIONS_MODEL,
  RIG_BODY_MODEL,
  RIG_CLIP_PACK_MODELS,
  RIG_LEAN_BODY_MODEL,
  packHasLeanBody,
  resolveRig,
  resolveWardrobe,
  type RigContent,
  type WardrobeResolution,
} from './resolve.js';

/**
 * The equipment slot that covers a wardrobe piece (q-art-handoff-hair-refit-live-head): hair
 * sits under any head-slot item, so a drawn helm hides it rather than the two clipping.
 */
const WARDROBE_COVERED_BY: Readonly<Partial<Record<WardrobeResolution['slot'], string>>> = {
  hair: 'head',
};

/** The default head attach node name a wardrobe piece parents to (fixture: `attach.head`). */
export const DEFAULT_HEAD_ATTACH = 'attach.head';

/** The add-slice of a `THREE.Object3D` (the attach parent). */
export interface AvatarAddTarget {
  add(object: Object3D): void;
}

export interface RigAvatarBuilderOptions {
  /** Presentation lookup (asset packs + appearance-option catalogue). */
  content: RigContent;
  /** Injected glTF loader (real: `createThreeGltfLoader()`; tests: a stub). */
  gltfLoad: GltfLoadFn;
  /** Static asset HTTP base for delivery URLs (`assetHttpBase()`); '' = same-origin. */
  httpBase?: string;
  /** GPU-resource disposer for a discarded mesh (default: three geometry/material dispose). */
  dispose?: (object: Object3D) => void;
  /** Non-fatal warning sink (default `console.warn`). */
  onWarn?: (message: string, error?: unknown) => void;
  /** Head attach node name (default `attach.head`); a miss falls back to the root. */
  headAttachName?: string;
  /**
   * Clips-only packs to append to the body's clips (default `RIG_CLIP_PACK_MODELS`, the
   * player's attack combos). The NPC placer passes its own (`npc-ambient`,
   * q-npc-ambient-animations) so players never download NPC clips and NPCs skip combat ones.
   */
  clipPackModels?: readonly string[];
  /**
   * Build on the lean first-frame body (`body-base-lean`) and fetch the rest of its clips as
   * the clips-only `body-actions` pack, beside `clipPackModels` (q-mobile-first-frame-clip-split;
   * the low tier, whose callers also defer the packs). A pack without the pair keeps the full
   * body. Default false: desktop loads `body-base` exactly as before.
   */
  leanBody?: boolean;
}

/** A built avatar: the rig root + the wardrobe meshes attached to it. */
export interface BuiltRigAvatar {
  /** The instantiated rig body root (morph applied, wardrobe attached). */
  root: Object3D;
  /** The wardrobe meshes attached this build (for later disposal by the caller). */
  wardrobe: Object3D[];
  /**
   * The rig's animation clips (AP5.S1) — `[]` when the delivered rig ships none,
   * in which case the caller simply leaves the avatar static. Shared immutable
   * data resolved by node name, so every avatar can be driven from these.
   */
  clips: readonly RigAnimationClip[];
}

function defaultWarn(message: string, error?: unknown): void {
  console.warn(`[rig-avatar] ${message}`, error ?? '');
}

/**
 * Builds a rig avatar from a character's appearance. Create it once against the
 * content bridge + injected loader, `build(appearance)` per avatar, and `dispose()`
 * on teardown. Every failure path returns `null` (capsule fallback) or skips the
 * piece (default look) — never throws into the render loop.
 */
export class RigAvatarBuilder {
  private readonly content: RigContent;
  private readonly loaders: PackLoaderCache;
  private readonly disposeObject: (object: Object3D) => void;
  private readonly onWarn: (message: string, error?: unknown) => void;
  private readonly headAttachName: string;
  private readonly clipPackModels: readonly string[];
  private readonly leanBody: boolean;
  private disposed = false;

  constructor(options: RigAvatarBuilderOptions) {
    this.content = options.content;
    this.loaders = new PackLoaderCache({
      gltfLoad: options.gltfLoad,
      httpBase: options.httpBase,
      onWarn: options.onWarn ?? defaultWarn,
    });
    this.disposeObject = options.dispose ?? disposeObject3D;
    this.onWarn = options.onWarn ?? defaultWarn;
    this.headAttachName = options.headAttachName ?? DEFAULT_HEAD_ATTACH;
    this.clipPackModels = options.clipPackModels ?? RIG_CLIP_PACK_MODELS;
    this.leanBody = options.leanBody ?? false;
  }

  /** Whether this builder uses the lean body for `pack` (asked for, and the pack has the pair). */
  private usesLeanBody(pack: AssetPackDef): boolean {
    return this.leanBody && packHasLeanBody(pack);
  }

  /**
   * Assemble the avatar for `appearance`, or `null` for the graceful capsule
   * fallback (no rig pack delivered, or the base body failed to load / was
   * superseded by a dispose). Applies the body-type morph and attaches the resolved
   * wardrobe; an unresolvable wardrobe piece is skipped (default look, spec §8).
   */
  async build(
    appearance: CharacterAppearance,
    options: { skipClipPacks?: boolean } = {},
  ): Promise<BuiltRigAvatar | null> {
    if (this.disposed) return null;
    const rig = resolveRig(this.content);
    if (rig === null) return null; // no rig pack ⇒ capsule fallback (caller keeps placeholder)

    const loader = this.loaders.loaderFor(rig.pack);
    const bodyModel = this.usesLeanBody(rig.pack) ? RIG_LEAN_BODY_MODEL : RIG_BODY_MODEL;
    let body: Object3D | null;
    try {
      body = await loader.instantiate(bodyModel, 0); // self/near band = lod0
    } catch (error) {
      this.onWarn(`rig body '${bodyModel}' failed to load`, error);
      return null; // load error ⇒ capsule fallback
    }
    if (body === null) return null; // unknown model / null template ⇒ capsule fallback
    if (this.disposed) {
      this.disposeObject(body); // superseded while loading ⇒ no orphan
      return null;
    }

    applyBodyMorph(body, appearance.bodyType);

    // AP5.S1: fetch the rig's clips alongside the body. `animationClips` falls back
    // to the base artifact when the instantiated LOD carries none (which is exactly
    // the `player-rig-v1` case), and caches, so this costs one load per session for
    // every player rather than one per avatar. A failure here must never lose the
    // avatar — an un-animated rig is still far better than the capsule fallback.
    let clips: readonly RigAnimationClip[] = [];
    try {
      clips = await loader.animationClips(bodyModel, 0);
    } catch (error) {
      this.onWarn(`rig clips for '${bodyModel}' failed to load; rig stays static`, error);
    }
    // AD-670: clips-only packs on the same skeleton (the library attack combos, AD-9008)
    // ship BESIDE the body (merged, the body would break the 4 MiB mobile budget). Their
    // tracks bind by bone name; a clip the body already carries keeps the body's copy;
    // a missing / failed pack just means fewer clips (never a lost avatar).
    // `skipClipPacks` (q-mobile-first-frame-defer-creatures-and-npc-clips): the caller fetches
    // them later through `clipPackClips()` and merges with `mergeClips`.
    if (options.skipClipPacks !== true) clips = mergeClips(clips, await this.clipPackClips());

    const wardrobe: Object3D[] = [];
    for (const piece of resolveWardrobe(this.content, appearance, rig.pack)) {
      let mesh: Object3D | null;
      try {
        mesh = await loader.instantiate(piece.assetId, 0);
      } catch (error) {
        this.onWarn(`wardrobe '${piece.assetId}' failed to load`, error);
        continue; // graceful: skip this piece (default look)
      }
      if (mesh === null) continue; // absent / unknown ⇒ default look (spec §8)
      if (this.disposed) {
        this.disposeObject(mesh);
        break;
      }
      // A helm drawn in the head slot hides the hair it would clip through (EquipmentRenderer).
      const coveredBy = WARDROBE_COVERED_BY[piece.slot];
      if (coveredBy !== undefined)
        mesh.userData = { ...mesh.userData, [COVERED_BY_SLOT]: coveredBy };
      const target = piece.attach === 'head' ? this.findHeadAttach(body) : body;
      target.add(mesh);
      wardrobe.push(mesh);
    }

    return { root: body, wardrobe, clips };
  }

  /**
   * The clips of every configured clips-only pack the rig pack carries, in order (cached by
   * the loader: one fetch per pack per session). A missing / failed pack contributes none.
   */
  async clipPackClips(): Promise<readonly RigAnimationClip[]> {
    const rig = resolveRig(this.content);
    if (rig === null || this.disposed) return [];
    const loader = this.loaders.loaderFor(rig.pack);
    let clips: readonly RigAnimationClip[] = [];
    const packModels = this.usesLeanBody(rig.pack)
      ? [RIG_BODY_ACTIONS_MODEL, ...this.clipPackModels]
      : this.clipPackModels;
    for (const packModel of packModels) {
      if (!rig.pack.assets?.some((asset) => asset.id === packModel)) continue;
      try {
        clips = mergeClips(clips, await loader.animationClips(packModel, 0));
      } catch (error) {
        this.onWarn(`rig clip pack '${packModel}' failed to load; its clips are skipped`, error);
      }
    }
    return clips;
  }

  /** Drop the loader caches (teardown). The caller disposes attached meshes it owns. */
  dispose(): void {
    this.disposed = true;
    this.loaders.clear();
  }

  /**
   * Find the head attach node by name in the loaded body, or fall back to the root. GLTFLoader
   * passes every node name through `PropertyBinding.sanitizeNodeName`, which strips '.', so the
   * served body's `attach.head` arrives as `attachhead` (H-1537): both spellings are accepted.
   */
  private findHeadAttach(root: Object3D): AvatarAddTarget {
    const names = new Set([
      this.headAttachName,
      PropertyBinding.sanitizeNodeName(this.headAttachName),
    ]);
    let found: Object3D | null = null;
    root.traverse((node) => {
      if (found === null && names.has(node.name)) found = node;
    });
    // Said out loud: the silent root fallback is how every hairstyle rendered bald for months.
    if (found === null)
      this.onWarn(`head socket '${this.headAttachName}' not found; attached at the body root`);
    return found ?? root;
  }
}

/** `base` plus each `extra` clip whose name `base` lacks (the body's own copy wins). */
export function mergeClips<T extends { readonly name: string }>(
  base: readonly T[],
  extra: readonly T[],
): readonly T[] {
  const have = new Set(base.map((clip) => clip.name));
  return [...base, ...extra.filter((clip) => !have.has(clip.name))];
}
